import template from 'header/search.j2';
import { mDom, mDomMaintainInputVals } from '../../../core/js/utils/renderUtils';
import Config from '../configs/config.js';
import {
    CLOSE_SITE_SEARCH,
    OPEN_SITE_SEARCH,
    editSiteSearch,
    saytSearch,
    saytSearchResults
} from '../actions/headerActions.js';

import { handleUtagEvent } from '../../../core/js/actions/initialActions.js';

import { sanitizeInput } from '../utils/validationUtils';
import { isTabPressed } from '../utils/keyUtils';

import { generate } from '../../../core/js/utils/componentUtils';
import { getDefaultOptions } from '../../../core/js/utils/a15componentUtils';
import { handleSearchSubmitAnalytics } from '@core/js/utils/searchUtils';

const domSelector = '.dom-site-search';
const searchInputSelector = '.js-site-search__input';

function firstRender(store) {
    this.render(store, this, {});
}

export function init(store, overrides = {}) {
    const config = {
        templateLocation: domSelector,
        className: 'dom-site-search',
        watch: ['core.sayt.header'],
        name: 'searchComponent',
        attachEvents: attachEvents,
        template: template,
        renderType: mDom,
        firstRender: firstRender,
        renderOptions: mDomMaintainInputVals
    };

    return generate(config, store, overrides, getDefaultOptions(store));
}

/**
 * Bootstrap the component onto the page
 * @returns {undefined}
 */
export function initialize(store) {
    attachEvents(store);
}

/**
 * Send the utag event when a user picks a SAYT suggestion
 * - sayt_input_term: value of search input before picking a suggestion
 * - sayt_suggestion_term: value of suggestion picked
 * - sayt_suggestion_index: index of suggestion picked
 * - sayt_suggestion_origin: 'sayt', 'trending', 'suggestedProduct' (See UW-11087)
 * @returns {undefined}
 */
export function handleSaytSearch(store, target) {
    const $target = $(target);
    store.dispatch(handleUtagEvent({
        event_name: 'sayt_search',
        event_type: 'linktrack',
        sayt_input_term: $(this.scopedSelector(searchInputSelector)).val().trim(),
        sayt_suggestion_term: $target.text().trim(),
        sayt_suggestion_index: $target.data('suggestion-index'),
        sayt_suggestion_origin: $target.data('suggestion-type')
    }, 'link'));
}

/**
 * Attach component events to DOM
 * @returns {undefined}
 */
export function attachEvents(store) {
    const deviceCharacteristics = this.getDeviceCharacteristics();
    const isLargeDevice = deviceCharacteristics.is_large || deviceCharacteristics.is_extra_large;

    // 'keyup' check for empty input and clear sayt results
    $(document).on(this.scopedEvent('keyup'), this.scopedSelector(searchInputSelector), (e) => {
        const isClearable = $(this.scopedSelector(searchInputSelector)).val().trim().length > 0;
        const searchSource = e.target.dataset.searchSource;

        store.dispatch(editSiteSearch(isClearable, searchSource));

        if ($(this.scopedSelector(searchInputSelector)).val().trim().length === 0) {
            store.dispatch(saytSearchResults([], [], searchSource, e));
        }
    });

    // 'mouseup' check for empty input and clear sayt results
    // using a debounce here in place of a timeout on the value check
    // when clicking the x, it only clears on mouseup also so .val of
    // the input hasn't cleared yet.
    $(document).on(this.scopedEvent('mouseup'), this.scopedSelector(searchInputSelector), _.debounce((e) => {
        if ($(this.scopedSelector(searchInputSelector)).val().trim().length === 0) {
            store.dispatch(saytSearchResults([], [], e.target.dataset.searchSource, e));
        }
    }, Config.search.debounce));

    // 'keypress' handles all key strokes for visual character keys
    $(document).on(this.scopedEvent('keypress'), this.scopedSelector(searchInputSelector), _.debounce((e) => {
        const searchTerm = $(this.scopedSelector(searchInputSelector)).val().trim();

        store.dispatch(saytSearch(searchTerm, e.target.dataset.searchSource, e));
    }, Config.search.debounce));

    // 'keydown' will handle non character strokes like delete or backspace changing input value.
    $(document).on(this.scopedEvent('keydown'), this.scopedSelector(searchInputSelector), _.debounce((e) => {
        const BACKSPACE = 8;
        const DELETE = 46;

        if ((e.keyCode === BACKSPACE || e.keyCode === DELETE) && $(this.scopedSelector(searchInputSelector)).val()) {
            const searchTerm = $(this.scopedSelector(searchInputSelector)).val();

            store.dispatch(saytSearch(searchTerm, e.target.dataset.searchSource, e));
        }
    }, Config.search.debounce));

    // another 'keydown' that we don't want debounced for the escape/return key
    // 'keydown' action focuses first list item on down arrow
    $(document).on(this.scopedEvent('keydown'), this.scopedSelector(searchInputSelector), (e) => {
        const ESCAPE = 27;
        const RETURN = 13;
        const DOWN = 40;

        if (e.keyCode === RETURN && $(this.scopedSelector(searchInputSelector)).val().trim() === '') {
            return false;
        }

        if (e.keyCode === ESCAPE) {
            $(this.scopedSelector(searchInputSelector)).val("").blur();
            store.dispatch(saytSearchResults([], [], e.target.dataset.searchSource, e));
        }

        if (e.keyCode === DOWN) {
            $('.js-search-suggestion:first').focus();
            e.preventDefault();
        }
    });

    // Another 'keydown' that is bound to the document.
    // This is specifically used to close the SAYT menu once you have hit tab while being focused on the last SAYT suggestion
    // We have to bind to the document because once you hit tab for the first time, you are no longer within the context of the scoped selector
    $(document).on(('keydown'), (e) => {
        if (!isTabPressed(e) || !isLargeDevice || $('.js-search-suggestion:last:focus').length < 1) { return; }
        const scopedSelector = $(this.scopedSelector(searchInputSelector));

        scopedSelector.val('').blur();
        store.dispatch(saytSearchResults([], [], scopedSelector.attr('data-search-source'), e));
    });

    // 'focus' opens search, and binds document 'click' event to clear results and close search
    $(document).on(this.scopedEvent('focus'), this.scopedSelector(searchInputSelector), (e) => {
        const $searchField = $(e.target);
        const searchSource = e.target.dataset.searchSource;
        const searchTerm = $searchField.val().trim();
        const inputHasValue = searchTerm.length > 0;

        // Note: vdom doesn't seem to merge the autocomplete, autocapitalize, or spellcheck attributes correctly.
        $searchField.attr({ 'autocomplete': 'off', 'autocapitalize': 'off', 'spellcheck': 'false', 'autocorrect': 'off' });

        if (inputHasValue) {
            store.dispatch(saytSearch(searchTerm, searchSource, e));
        }

        store.dispatch(editSiteSearch(inputHasValue, searchSource));

        const siteSearchDocumentListener = (e) => {
            if (!$(e.target).parents(this.templateLocation).length && !$(e.target).hasClass(this.className)) {
                $(document).off('click', siteSearchDocumentListener);
                store.dispatch(saytSearchResults([], [], searchSource, e));
                if (this.templateLocation === domSelector) {
                    $.event.trigger({
                        type: CLOSE_SITE_SEARCH
                    });
                }
            }
        };

        $(document).on('click', siteSearchDocumentListener);

        // UW-6240 On mobile prevent users search term from being cleared upon re-tap of the text
        // input field once the user has collapsed the keyboard
        if (!deviceCharacteristics.is_touch) {
            if (this.templateLocation === domSelector) {
                $.event.trigger({
                    type: OPEN_SITE_SEARCH
                });
            }
        }
    });

    $(document).on(this.scopedEvent('touchend click'), '.js-clear-search-input', (e) => {
        $(`${domSelector} ${searchInputSelector}`).val('');
        store.dispatch(saytSearchResults([], [], 'header'));
        store.dispatch(editSiteSearch(false, 'header'));
    });

    $(document).on(this.scopedEvent('submit'), this.scopedSelector('.js-site-search__form'), (e) => {
        e.preventDefault();
        const $input = $(e.target).find(searchInputSelector);
        const newVal = sanitizeInput($input.val());

        $input.val(newVal);

        if (!newVal) {
            return false;
        }

        handleSearchSubmitAnalytics(store, newVal);

        e.target.submit();
    });

    // 'keydown' allows users to navgiate through sayt suggestion items using up and down arrows
    $(document).on(this.scopedEvent('keydown'), this.scopedSelector('.js-site-search-sayt'), (e) => {
        const UP = 38;
        const DOWN = 40;

        if (e.keyCode === UP) {
            e.preventDefault();
            $('.js-search-suggestion:focus').closest('.js-site-search-sayt__li').prev().find('.js-search-suggestion').focus();
        }

        if (e.keyCode === DOWN) {
            e.preventDefault();
            $('.js-search-suggestion:focus').closest('.js-site-search-sayt__li').next().find('.js-search-suggestion').focus();
        }
    });

    // 'keydown' on space/right arrow allows user to enter search term but not apply
    $(document).on(this.scopedEvent('keydown'), this.scopedSelector('.js-search-suggestion'), (e) => {
        const SPACE = 32;
        const RIGHT = 39;
        const ESCAPE = 27;

        if (e.keyCode === ESCAPE) {
            const scopedSelector = $(this.scopedSelector(searchInputSelector));

            scopedSelector.val("").blur();
            store.dispatch(saytSearchResults([], [], scopedSelector.attr('data-search-source'), e));
        }

        if (e.keyCode === SPACE || e.keyCode === RIGHT) {
            e.preventDefault();
            const searchTerm = $(e.target).text().trim();
            const searchSource = $(this.scopedSelector(searchInputSelector)).val().trim();
            const searchFieldHeader = $(searchInputSelector);
            const inputHasValueHeader = searchTerm.length > 0;

            store.dispatch(saytSearch(searchTerm, searchSource, e));

            if (inputHasValueHeader) {
                $(searchFieldHeader).val(searchTerm).blur();
            }
        }
    });

    // send SAYT GA event
    $(document).on(this.scopedEvent('click'), this.scopedSelector('.js-search-suggestion'), (e) => {
        handleSaytSearch.bind(this)(store, e.target);
    });
}
